// actions-table.ts — Table de la liste des actions (#actions) : lignes via le
// composant partagé `componentActionRow`, en-tête triable, colonne RACI à
// menu de tri, barre « tout cocher / décocher », état vide, clic sur une
// ligne (sélection, ouverture de l'éditeur, tri), aperçu des notes au survol.
//
// Troisième tranche de l'extraction des closures de `renderActionsPage`
// (audit dette technique 2026-09-07, point 16). Le rendu est une fonction
// pure des actions filtrées, du tri et des maps de référence ; le câblage
// reçoit la sélection et cinq rappels vers la page. Le tri est un petit
// objet `SortState` : la page en reste propriétaire (persistance par tenant)
// et applique le résultat de `toggleSort`, testé sans DOM.
//
// La navigation vers l'éditeur (`#action/:id`) pose en sessionStorage le
// hash de retour et la sélection courante, comme avant : le bouton Fermer de
// la page d'édition revient au contexte exact (filtres, mode rattachement).

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

/** Clic sur un critère : même clé → inverse le sens, autre clé → ascendant. */
export function toggleSort(sort: SortState, key: string): SortState {
  if (sort.key === key) return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

/** En-tête RACI : tri géré via dropdown (Réalisateurs / Approbateur /
 * Consultés). Affiche le critère courant + flèche si l'un des 3 est actif.
 * sortKey 'owner' = approver (A) ; sortKey 'actors' = performers (R). */
export function raciHeaderHtml(sort: SortState): string {
  const raciActive = ['owner', 'actors', 'consulted'].includes(sort.key);
  const raciLabel = raciActive
    ? t('pageActions.col.raciLabel', {
        letter: sort.key === 'owner' ? 'A' : sort.key === 'actors' ? 'R' : 'C',
        arrow: sort.dir === 'asc' ? '↑' : '↓',
      })
    : t('pageActions.col.raciDefault');
  return (
    `<th class="action-raci-th sortable${raciActive ? ' sorted' : ''}"` +
    ` style="cursor:pointer;user-select:none;white-space:nowrap;position:relative">${esc(raciLabel)}</th>`
  );
}

export function emptyStateHtml(): string {
  return `<div class="card"><div class="empty" style="padding:32px;text-align:center">
        <div>${esc(t('pageActions.empty.line'))}</div>
        <button id="af-reset-empty" class="btn btn-secondary btn-sm" style="margin-top:12px">${esc(t('pageActions.empty.btnReset'))}</button>
      </div></div>`;
}

/** Toolbar de sélection en masse au-dessus de la table, alignée à gauche
 * pour rester sous la colonne checkbox. Agit sur la liste affichée. */
export function selectionToolbarHtml(): string {
  return (
    `<div id="actions-sel-toolbar" style="display:flex;align-items:center;gap:8px;padding:6px 8px 8px">` +
    `<button type="button" id="btn-actions-select-all"
        class="btn btn-secondary btn-sm" style="padding:2px 10px;font-size:11px">${esc(
          t('pageInviteCommittee.btnCheckAll'),
        )}</button>` +
    `<button type="button" id="btn-actions-unselect-all"
        class="btn btn-secondary btn-sm" style="padding:2px 10px;font-size:11px">${esc(
          t('pageInviteCommittee.btnUncheckAll'),
        )}</button>` +
    `</div>`
  );
}

export interface TableRenderContext {
  statuses: ReadonlyArray<unknown>;
  userMap: Record<number, unknown>;
  projectMap: Record<number, unknown>;
  userId: number | null;
  userTeamIds: ReadonlyArray<number>;
  selectedIds: ReadonlySet<number>;
}

/** Card complète (toolbar + table) ou état vide. Les lignes sont déléguées au
 * composant partagé `componentActionRow.buildActionRowHtml` : les deux pages
 * (#actions, projet) partagent la même source de vérité. */
export function actionsTableHtml(
  filtered: ReadonlyArray<{ id?: number }>,
  sort: SortState,
  ctx: TableRenderContext,
): string {
  if (!filtered.length) return emptyStateHtml();
  let rowsHtml = '';
  for (const a of filtered) {
    rowsHtml += window.componentActionRow.buildActionRowHtml(
      a as never,
      {
        statuses: ctx.statuses,
        userMap: ctx.userMap,
        projectMap: ctx.projectMap,
        readOnly: false,
        currentUserId: ctx.userId,
        userTeamIds: ctx.userTeamIds,
        showCheckbox: true,
        selectedIds: ctx.selectedIds,
      } as never,
    );
  }
  const headersHtml = window.componentActionRow.buildActionHeadersHtml({
    // En-têtes triables : rendu partagé avec les autres tables d'actions.
    sortFn: (key: string, label: string) =>
      window.componentActionRow.buildSortTh(key, label, sort.key, sort.dir),
    raciHeader: raciHeaderHtml(sort),
    showCheckbox: true,
  });
  const toolbarHtml = selectionToolbarHtml();
  return `<div class="card">${toolbarHtml}<div class="table-wrap"><table id="actions-list-table">
      <thead><tr>${headersHtml}</tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table></div></div>`;
}

/** Menu déroulant de tri RACI sous l'en-tête cliqué. Trois options :
 * Réalisateurs (R, 'actors'), Approbateur (A, 'owner'), Consultés (C,
 * 'consulted'). L'option active affiche une flèche ↑/↓ et re-cliquer dessus
 * inverse la direction. Les clés 'owner'/'actors' sont conservées telles
 * quelles pour ne pas casser l'URL. */
export function openRaciSortMenu(
  thEl: HTMLElement,
  sort: SortState,
  onSort: (next: SortState) => void,
): void {
  document.querySelectorAll('.raci-sort-menu').forEach((m) => m.remove());
  const rect = thEl.getBoundingClientRect();
  const menu = document.createElement('div');
  menu.className = 'raci-sort-menu';
  menu.style.cssText =
    `position:fixed;top:${rect.bottom + 4}px;left:${rect.left}px;` +
    `background:var(--bg);border:1px solid var(--border2);border-radius:var(--radius);` +
    `box-shadow:0 4px 12px rgba(0,0,0,0.12);padding:4px;z-index:1000;min-width:170px`;
  const opts = [
    { key: 'owner', label: t('pageActions.raciSort.owner') },
    { key: 'actors', label: t('pageActions.raciSort.actors') },
    { key: 'consulted', label: t('pageActions.raciSort.consulted') },
  ];
  menu.innerHTML = opts
    .map((o) => {
      const active = sort.key === o.key;
      const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
      return `<div class="raci-sort-opt menu-hover-bg2${active ? ' is-active' : ''}" data-raci-key="${o.key}"
        style="padding:6px 10px;cursor:pointer;font-size:12px;border-radius:4px;
        ${active ? 'font-weight:600' : ''}"
        >${esc(o.label)}${arrow}</div>`;
    })
    .join('');
  document.body.appendChild(menu);
  menu.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    const opt = target?.closest<HTMLElement>('.raci-sort-opt');
    if (!opt) return;
    const key = opt.dataset['raciKey'] || '';
    menu.remove();
    onSort(toggleSort(sort, key));
  });
  setTimeout(() => {
    const closeOnOutside = (ev: Event): void => {
      const node = ev.target as Node | null;
      if (node && !menu.contains(node)) {
        menu.remove();
        document.removeEventListener('click', closeOnOutside, true);
      }
    };
    document.addEventListener('click', closeOnOutside, true);
    // Filet de sécurité navigation : au prochain dispatch du router,
    // on retire le listener et on enlève le menu si encore présent.
    registerPageDisposer(() => {
      document.removeEventListener('click', closeOnOutside, true);
      try {
        menu.remove();
      } catch {
        /* déjà détaché */
      }
    });
  }, 0);
}

// ── Survol du compteur de notes : 5 dernières notes, chargées à la demande ──
// Les notes ne sont PAS dans le payload de la liste (flux liste léger). On
// les récupère au survol via GET /actions/:id/notes (qui applique déjà
// can_see_action → confidentialité respectée), avec cache par action.

interface NoteLite {
  created_at?: string | null;
  author_name?: string | null;
  comment?: string;
}

export interface NotesHover {
  wire(table: HTMLElement): void;
}

/** Une instance par page : le cache des notes et le popover courant vivent
 * ici, pas dans la page. */
export function createNotesHover(): NotesHover {
  const cache = new Map<number, NoteLite[]>();
  let popover: HTMLElement | null = null;
  let hideTimer: number | null = null;
  const hide = (): void => {
    if (popover) {
      popover.remove();
      popover = null;
    }
  };
  const scheduleHide = (): void => {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hide, 200);
  };
  const cancelHide = (): void => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };
  const show = (badge: HTMLElement, notes: NoteLite[]): void => {
    hide();
    const last5 = notes.slice(-5).reverse(); // 5 plus récentes, newest-first
    const bodyHtml = last5.length
      ? last5
          .map((n) => {
            const d = n.created_at ? esc(n.created_at.split('T')[0] ?? '') : '';
            const who = esc(n.author_name || '—');
            const txt = esc((n.comment || '').trim().slice(0, 240));
            return (
              `<div style="padding:4px 0;border-top:1px solid var(--border)">` +
              `<div style="font-size:10px;color:var(--text3)">${d} · ${who}</div>` +
              `<div style="white-space:pre-wrap">${txt}</div></div>`
            );
          })
          .join('')
      : `<div style="color:var(--text3)">${esc(t('pageActions.notes.none'))}</div>`;
    const pop = document.createElement('div');
    pop.className = 'notes-hover-popover';
    const r = badge.getBoundingClientRect();
    pop.style.cssText =
      'position:fixed;z-index:1000;background:var(--bg2);border:1px solid var(--border2);' +
      'border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);padding:8px 10px;' +
      'max-width:340px;max-height:260px;overflow:auto;font-size:12px;line-height:1.4;' +
      `left:${Math.round(r.left)}px;top:${Math.round(r.bottom + 4)}px`;
    pop.innerHTML = `<div style="font-weight:600;margin-bottom:2px">${esc(t('pageActions.notes.lastFive'))}</div>${bodyHtml}`;
    pop.addEventListener('mouseenter', cancelHide);
    pop.addEventListener('mouseleave', hide);
    document.body.appendChild(pop);
    popover = pop;
  };
  function wire(table: HTMLElement): void {
    table.addEventListener('mouseover', (e: Event) => {
      const badge = (e.target as HTMLElement | null)?.closest<HTMLElement>('.notes-count-badge');
      if (!badge) return;
      cancelHide();
      const aid = parseInt(badge.dataset['actionId'] || '0');
      if (!aid) return;
      const cached = cache.get(aid);
      if (cached) {
        show(badge, cached);
        return;
      }
      void api
        .getActionNotes(aid)
        .then((notes) => {
          const arr = (notes as NoteLite[]) || [];
          cache.set(aid, arr);
          if (badge.matches(':hover')) show(badge, arr);
        })
        .catch(() => {
          /* note: échec silencieux — le badge reste informatif via son nombre */
        });
    });
    table.addEventListener('mouseout', (e: Event) => {
      const badge = (e.target as HTMLElement | null)?.closest<HTMLElement>('.notes-count-badge');
      if (badge) scheduleHide();
    });
  }
  return { wire };
}

// ── Câblage ──────────────────────────────────────────────────────────────

export interface TableWireContext<A extends { id?: number }> {
  selectedIds: Set<number>;
  findAction: (id: number) => A | undefined;
  notesHover: NotesHover;
  /** La sélection a changé (case cochée, tout cocher/décocher) : la page
   * rafraîchit la barre d'actions de masse. */
  onSelectionChange: () => void;
  onSort: (next: SortState) => void;
  /** Avant la navigation vers l'éditeur : la page marque l'action comme vue. */
  onOpen: (action: A) => void;
  /** Bouton « Réinitialiser les filtres » de l'état vide. */
  onResetFilters: () => void;
}

/** Navigation vers l'éditeur en mémorisant le contexte de retour : hash
 * courant (filtres, mode rattachement) et sélection, consommés par
 * page-action.ts (consumeBackHash) et par la liste au retour. */
export function openActionEditor(actionId: number, selectedIds: ReadonlySet<number>): void {
  try {
    sessionStorage.setItem('actionEditor:backHash', window.location.hash);
    sessionStorage.setItem('actions:pendingSelection', JSON.stringify([...selectedIds]));
  } catch {
    /* sessionStorage indisponible — fallback #actions au retour */
  }
  window.location.hash = '#action/' + actionId;
}

export function wireActionsTable<A extends { id?: number }>(
  root: ParentNode,
  sort: SortState,
  ctx: TableWireContext<A>,
): void {
  // Bouton "Réinitialiser les filtres" affiché en cas de résultat vide.
  root.querySelector('#af-reset-empty')?.addEventListener('click', () => ctx.onResetFilters());
  // Cocher / décocher toutes les actions actuellement visibles. Le delta est
  // appliqué à `selectedIds` puis la page rafraîchit la bulk-bar.
  const toggleVisible = (check: boolean): void => {
    root
      .querySelectorAll<HTMLInputElement>('#actions-list-table .action-select-checkbox')
      .forEach((cb) => {
        const aid = parseInt(cb.dataset['actionId'] || '0');
        if (!aid) return;
        cb.checked = check;
        if (check) ctx.selectedIds.add(aid);
        else ctx.selectedIds.delete(aid);
      });
    ctx.onSelectionChange();
  };
  root
    .querySelector('#btn-actions-select-all')
    ?.addEventListener('click', () => toggleVisible(true));
  root
    .querySelector('#btn-actions-unselect-all')
    ?.addEventListener('click', () => toggleVisible(false));

  const table = root.querySelector<HTMLElement>('#actions-list-table');
  if (!table) return;
  ctx.notesHover.wire(table);

  const open = (aid: number): void => {
    const action = ctx.findAction(aid);
    if (action) ctx.onOpen(action);
    // RefactoUX/Actions (mai 2026) : depuis la liste, on navigue vers la page
    // d'édition `#action/:id` (plus de popup). Le retour ramène à la liste
    // avec son état (filtres, tri), re-rendue par le hashchange.
    if (action && aid > 0) openActionEditor(aid, ctx.selectedIds);
  };

  table.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Fusion : interception des cases à cocher de sélection. Avant le
    // textCell / sortTh / editBtn pour ne pas déclencher la navigation
    // quand on coche une ligne.
    const selectCb = target.closest<HTMLInputElement>('.action-select-checkbox');
    if (selectCb) {
      e.stopPropagation();
      const aid = parseInt(selectCb.dataset['actionId'] || '0');
      if (aid > 0) {
        if (selectCb.checked) ctx.selectedIds.add(aid);
        else ctx.selectedIds.delete(aid);
        ctx.onSelectionChange();
      }
      return;
    }
    const textCell = target.closest<HTMLElement>('.action-text-cell[data-action-id]');
    if (textCell && !target.closest('button')) {
      open(parseInt(textCell.dataset['actionId'] || '0'));
      return;
    }
    const raciTh = target.closest<HTMLElement>('.action-raci-th');
    if (raciTh) {
      openRaciSortMenu(raciTh, sort, ctx.onSort);
      return;
    }
    const sortTh = target.closest<HTMLElement>('th[data-sort]');
    if (sortTh) {
      ctx.onSort(toggleSort(sort, sortTh.dataset['sort'] || ''));
      return;
    }
    // Crayon ✎ : même navigation que le clic sur ID / description.
    const editBtn = target.closest<HTMLElement>('.edit-action-btn');
    if (editBtn) open(parseInt(editBtn.dataset['actionId'] || '0'));
  });
}
